import * as React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import AddNew from 'components/buttons/add-new/AddNew';
import ActionButton from 'components/buttons/action-button/ActionButton';
import DeliverableLook from './deliverable-look/DeliverableLook';
import Input from 'components/inputs/input/Input';

import { __t, applyTranslations } from 'utils/language';

import { updateLooks } from 'modules/deliverables/reducers/deliverableContentApproval';

import { DeliverableLook as DeliverableLookAlias, Subcampaign } from 'types';

import './deliverable-looks.scss';

applyTranslations(require('./deliverable-looks.i18n.json'));

type OwnProps = {
  subcampaign: Subcampaign,
  onClose: () => void
}

type StateProps = {
  campaignLooks: Array<DeliverableLookAlias>
}

type DispatchProps = {
  dispatchUpdateLooks: (campaignId: number, looks: Array<DeliverableLookAlias>) => Promise<void>
}

type State = {
  mode: 'delete' | 'add' | 'edit' | null,
  looks: Array<DeliverableLookAlias>,
  isModified: boolean,
  lookName: string,
  error: string,
  selectedLookId: number | null
}

type Props = OwnProps & StateProps & DispatchProps;

const msgKey = 'deliverable_looks';

class DeliverableLooks extends React.PureComponent<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      looks: [...props.campaignLooks],
      isModified: false,
      mode: null,
      lookName: '',
      error: '',
      selectedLookId: null
    };
  }

  addEditLook = () => {
    const { lookName, mode, selectedLookId } = this.state;

    if (lookName && mode === 'add') {
      this.setState(prevState => ({
        ...prevState,
        mode: null,
        lookName: '',
        looks: [...prevState.looks, {
          title: prevState.lookName,
          isTag: false,
          id: Math.random()
        }]
      }));
    }

    if (lookName && mode === 'edit' && selectedLookId) {
      this.setState(prevState => ({
        ...prevState,
        mode: null,
        lookName: '',
        looks: prevState.looks.map(look => {
          if (look.id === selectedLookId) {
            return { ...look, title: lookName };
          }

          return look;
        })
      }));
    }
  };

  getInputClasses(): string {
    const { error } = this.state;

    return classNames({
      'deliverable-looks__add-new-look-input-error': error
    });
  }

  handleAddNewLook = () => {
    this.setState({ mode: 'add', isModified: true });
  };

  handleSave = () => {
    const { lookName, mode, looks } = this.state;
    const { dispatchUpdateLooks, subcampaign } = this.props;
    const { campaignId } = subcampaign;

    if (mode === 'add' && !lookName) {
      this.setState({ error: __t(`${msgKey}.error`) });

      return;
    }

    if (mode === 'add' && lookName) {
      const isLookRepeated = looks.find(look => look.title === lookName);

      this.setState({ error: isLookRepeated ? __t(`${msgKey}.unique_look_error`) : '' });

      return;
    }

    dispatchUpdateLooks(campaignId, looks)
      .then(() => {
        this.setState({
          isModified: false,
          looks: [...this.props.campaignLooks]
        });
      });
  };

  handleDelete = () => {
    const { selectedLookId } = this.state;

    this.setState(prevState => ({
      ...prevState,
      looks: prevState.looks.filter(look => look.id !== selectedLookId),
      selectedLookId: null,
      mode: null
    }));
  };

  handleEditLook = (look: DeliverableLookAlias) => {
    this.setState({
      selectedLookId: look.id,
      mode: 'edit',
      lookName: look.title,
      isModified: true
    });
  };

  handleDeleteLook = (look: DeliverableLookAlias) => {
    this.setState({
      selectedLookId: look.id,
      mode: 'delete',
      isModified: true
    });
  };

  handleCancelChanges = () => {
    const { campaignLooks } = this.props;

    this.setState({
      isModified: false,
      looks: campaignLooks,
      mode: null,
      lookName: '',
      error: '',
      selectedLookId: null
    });
  };

  handleFieldChange = (type: string, value) => {
    this.setState(prevState => ({
      ...prevState,
      [type]: value,
      isModified: true,
      error: ''
    }));
  };

  renderActions = () => {
    const { onClose } = this.props;
    const { isModified, mode } = this.state;

    return (
      <div className="deliverable-looks__action">
        {
          !isModified && (
            <ActionButton
              color="secondary"
              onClick={onClose}
            >
              {__t(`${msgKey}.buttons.close`)}
            </ActionButton>
          )
        }
        {
          isModified && (
            <React.Fragment>
              <ActionButton
                color="tertiary"
                onClick={this.handleCancelChanges}
              >
                {__t(`${msgKey}.buttons.cancel`)}
              </ActionButton>
              {
                mode === 'delete' ? (
                  <ActionButton
                    color="primary"
                    onClick={this.handleDelete}
                  >
                    {__t(`${msgKey}.buttons.delete`)}
                  </ActionButton>

                ) : (
                  <ActionButton
                    color="primary"
                    onClick={this.handleSave}
                  >
                    {__t(`${msgKey}.buttons.save`)}
                  </ActionButton>
                )
              }
            </React.Fragment>
          )
        }
      </div>
    );
  };

  renderHeader = () => (
    <React.Fragment>
      <div className="deliverable-looks__header">
        {__t(`${msgKey}.header`)}
      </div>
      <div className="deliverable-looks__sub-header">
        {__t(`${msgKey}.sub_header`)}
      </div>
    </React.Fragment>
  );

  renderAddLook(): React.ReactNode {
    return (
      <div className="deliverable-looks__new-look">
        <AddNew
          text={__t(`${msgKey}.add_look`)}
          icon={{
            name: 'plus-inside-circle-custom-bg-white-fg',
            width: 16,
            height: 16,
            distance: 'double'
          }}
          color="grey"
          size="small"
          onClick={this.handleAddNewLook}
        />
      </div>
    );
  }

  renderDeleteMode(): React.ReactNode {
    const { looks, selectedLookId } = this.state;
    const selectedLook = looks.find(look => look.id === selectedLookId);

    return (
      <div className="deliverable-looks">
        {this.renderHeader()}
        <div className="deliverable-looks__delete-mode-main">
          <div className="deliverable-looks__delete-mode-header">
            {__t(`${msgKey}.delete_look_copy.header`, { look: selectedLook?.title })}
          </div>
          <div className="deliverable-looks__delete-mode-sub-header">
            {__t(`${msgKey}.delete_look_copy.sub_header`)}
          </div>
        </div>
        {this.renderActions()}
      </div>
    );
  }

  renderAddEditLook(type: 'add' | 'edit', look?: DeliverableLookAlias): React.ReactNode {
    const { lookName, error } = this.state;
    const inputClasses = this.getInputClasses();

    return (
      <div
        key={`deliverable-looks__look--${look?.title}-${look?.id || type}`}
        className="deliverable-looks__add-new-look"
      >
        <div className="deliverable-looks__add-new-look-header">
          {__t(`${msgKey}.${type}_look_header`)}
        </div>
        <Input
          name="title"
          width="responsive"
          value={lookName}
          className={inputClasses}
          autoFocus
          onChange={(event) => this.handleFieldChange('lookName', event.target.value)}
          onBlur={this.addEditLook}
        />
        {
          error && (
            <div className="deliverable-looks__add-new-look-error">
              {error}
            </div>
          )
        }
      </div>
    );
  }

  renderLook(look: DeliverableLookAlias): React.ReactNode {
    return (
      <DeliverableLook
        key={`deliverable-looks__look--${look.title}-${look.id}`}
        look={look}
        onEdit={this.handleEditLook}
        onDelete={this.handleDeleteLook}
      />
    );
  }

  render() {
    const { mode, looks, selectedLookId } = this.state;

    if (selectedLookId && mode === 'delete') {
      return this.renderDeleteMode();
    }

    const listClasses = classNames('deliverable-looks__list', {
      'deliverable-looks__list--add-mode': mode === 'add'
    });

    return (
      <div className="deliverable-looks">
        <div className="deliverable-looks__main">
          {this.renderHeader()}
          {this.renderAddLook()}
          {
            mode === 'add' &&
              this.renderAddEditLook('add')
          }
          <div className={listClasses}>
            {
              looks.map(look => (
                selectedLookId === look.id && mode === 'edit'
                  ? this.renderAddEditLook('edit', look)
                  : this.renderLook(look)
              ))
            }
          </div>
        </div>
        {this.renderActions()}
      </div>
    );
  }
}

const mapStateToProps = ({ deliverableContentApproval }, { subcampaign }): StateProps => ({
  campaignLooks: deliverableContentApproval.looks[subcampaign?.campaign?.id] || []
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateLooks: (campaignId: number, looks: Array<DeliverableLookAlias>) => (
    dispatch(updateLooks(campaignId, looks))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableLooks);
